import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import styles from './ProducerBookmarksPage.module.css';
import ProducerContentContainer from '../layout/ProducerContentContainer';
import ActorDetailModal from '../components/ActorDetailModal';
import FadeImage from '../../../shared/components/FadeImage';
import LoadingSpinner from '../../../shared/components/LoadingSpinner';
import { getBoardAccentBackground } from '../../../api/boardApi';
import {
  createBoard, updateBoard, deleteBoard,
  getBoardItems, getAllSavedItems, removeBoardItem, moveBoardItem, addBoardItem, getBoardPickerOptions,
} from '../../../api/boardApi';
import { getTalentDetail, getTalentMediaPhotos } from '../../../api/talentApi';
import { useSavedItems } from '../../../contexts/SavedItemsContext';


const COLORS = ['#E94934', '#FFD92B', '#FFA40D', '#3B82F6'];
const ICONS = ['📚', '🥊', '✨', '🎬'];
const GENDER_MAP = { MALE: '남성', FEMALE: '여성' };
const PREFERRED_DEFAULT_BOARD_KEY = 'producer-bookmark-preferred-default';

function mapBoardItem(item) {
  // board item: { id, boardId, targetType, targetId, talentProfile?, createdAt }
  const talent = item.talentProfile ?? item.talent ?? {};
  const medias = talent.medias || [];
  const profileCloseUpUrl = medias.find(m => m.photoType === 'PROFILE_CLOSE_UP' && m.url)?.url ?? null;
  // PROFILE_CLOSE_UP 미디어가 실제로 있을 때만 프로필 이미지 사용
  const photoUrl = profileCloseUpUrl
    ?? (medias.length === 0 ? (talent.profileImageUrl ?? null) : null);
  return {
    archiveItemId: item.id,  // board item id — used for removeBoardItem
    boardId: item.boardId ?? null,
    id: item.targetId ?? talent.id ?? talent.talentProfileId,
    name: talent.stageName || talent.name || '—',
    gender: GENDER_MAP[talent.gender] || talent.gender || '—',
    age: talent.age ? `${talent.age}세` : '—',
    height: talent.heightCm ? `${talent.heightCm}cm` : '—',
    tags: (talent.skills || []).map(s => s.name || s.skillName || s).filter(Boolean),
    portfolio: talent.bio || '',
    savedAt: item.createdAt ? item.createdAt.slice(0, 10).replace(/-/g, '. ') : '—',
    photoUrl,
  };
}

function ActorCard({ actor, boards = [], currentBoardId = null, onDetail, onRemove, onMove }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);
  const menuRef = useRef(null);
  const movableBoards = useMemo(
    () => boards.filter((board) => String(board.id) !== String(currentBoardId ?? actor.boardId ?? null)),
    [actor.boardId, boards, currentBoardId]
  );

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!menuRef.current?.contains(e.target)) {
        setMenuOpen(false);
        setMoveOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className={styles.actorCard}>
      <div className={styles.actorCardImage}>
        <FadeImage
          src={actor.photoUrl}
          alt={actor.name}
          size="medium"
          placeholder="talent"
          width={220}
          height={293}
        />
        <div ref={menuRef} className={styles.actorCardMenuWrap}>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setMenuOpen((prev) => !prev);
              setMoveOpen(false);
            }}
            className={styles.actorCardMenuBtn}
            title="보관함 메뉴"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <circle cx="5" cy="12" r="1.5" fill="#888896" />
              <circle cx="12" cy="12" r="1.5" fill="#888896" />
              <circle cx="19" cy="12" r="1.5" fill="#888896" />
            </svg>
          </button>
          {menuOpen && (
            <div className={styles.actorCardMenuDropdown} onClick={(e) => e.stopPropagation()}>
              {movableBoards.length > 0 && (
                <button type="button" onClick={() => setMoveOpen((prev) => !prev)} className={styles.actorCardMenuOption}>
                  다른 보관함으로 이동
                </button>
              )}
              <button type="button" onClick={() => onRemove(actor)} className={styles.actorCardMenuOptionDelete}>
                삭제
              </button>
              {moveOpen && movableBoards.length > 0 && (
                <div className={styles.actorCardMoveList}>
                  {movableBoards.map((board) => (
                    <button
                      key={board.id}
                      type="button"
                      onClick={() => {
                        onMove(actor, board.id);
                        setMoveOpen(false);
                        setMenuOpen(false);
                      }}
                      className={styles.actorCardMoveOption}
                    >
                      <span
                        style={{
                          width: '24px',
                          height: '24px',
                          borderRadius: '8px',
                          background: board.accentBackground || getBoardAccentBackground(board.color),
                          color: board.color || '#888896',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '14px',
                          flexShrink: 0,
                        }}
                      >
                        {board.emoji || board.icon || '📁'}
                      </span>
                      {board.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        {actor.tags.length > 0 && (
          <div className={styles.actorCardTagOverlay}>
            {actor.tags.map(tag => (
              <span key={tag} className={styles.actorCardOverlayTag}>{tag}</span>
            ))}
          </div>
        )}
      </div>
      <div className={styles.actorCardBody} onClick={() => onDetail(actor.id)}>
        <div className={styles.actorCardName}>{actor.name}</div>
        <div className={styles.actorCardMeta}>
          <span className={styles.actorCardMetaText}>{actor.gender}</span>
          <span className={styles.actorCardMetaDivider}>|</span>
          <span className={styles.actorCardMetaText}>{actor.age}</span>
          <span className={styles.actorCardMetaDivider}>|</span>
          <span className={styles.actorCardMetaText}>{actor.height}</span>
        </div>
      </div>
    </div>
  );
}

function NewBoardModal({ onClose, onCreate, initialData = null }) {
  const isEdit = !!initialData;
  const [name, setName] = useState(initialData?.name || '');
  const [desc, setDesc] = useState(initialData?.desc || '');
  const [color, setColor] = useState(initialData?.color || COLORS[0]);
  const [icon, setIcon] = useState(initialData?.emoji || initialData?.icon || ICONS[0]);

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className={styles.modalBox}>
        <button onClick={onClose} className={styles.modalCloseBtn}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18M6 6l12 12" stroke="#888896" strokeWidth="2" strokeLinecap="round"/></svg>
        </button>
        <h2 className={styles.modalTitle}>{isEdit ? '보드 수정하기' : '새 보드 만들기'}</h2>
        <div className={styles.modalFields}>
          <div>
            <div className={styles.modalFieldLabel}>보드 이름 <span className={styles.modalFieldLabelRequired}>*</span></div>
            <input value={name} onChange={e => setName(e.target.value)} placeholder="예 : 관심 공고, 지원 예정" className={styles.modalInput} />
          </div>
          <div>
            <div className={styles.modalFieldLabel}>설명</div>
            <textarea value={desc} onChange={e => setDesc(e.target.value)} placeholder="이 보드에 대한 간단한 설명을 입력하세요." rows={5} className={styles.modalTextarea} />
          </div>
          <div>
            <div style={{ fontFamily: 'Pretendard, sans-serif', fontSize: '15px', fontWeight: 600, letterSpacing: '-0.04em', color: '#090909', lineHeight: '140%', marginBottom: '12px' }}>색상</div>
            <div className={styles.modalColorRow}>
              {COLORS.map(c => (
                <div key={c} onClick={() => setColor(c)} className={styles.modalColorSwatch}
                  style={{ background: c, border: color === c ? '3px solid #090909' : '3px solid transparent' }}>
                  {color === c && <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M20 6L9 17l-5-5" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                </div>
              ))}
            </div>
          </div>
          <div>
            <div style={{ fontFamily: 'Pretendard, sans-serif', fontSize: '15px', fontWeight: 600, letterSpacing: '-0.04em', color: '#090909', lineHeight: '140%', marginBottom: '12px' }}>아이콘</div>
            <div className={styles.modalIconRow}>
              {ICONS.map(ic => (
                <div key={ic} onClick={() => setIcon(ic)} className={styles.modalIconItem}
                  style={{ border: icon === ic ? '2px solid #090909' : '2px solid transparent' }}>
                  {ic}
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className={styles.modalFooter}>
          <button onClick={onClose} className={styles.modalCancelBtn}>취소</button>
          <button
            onClick={() => { onCreate({ name, desc, color, icon }); onClose(); }}
            className={styles.modalCreateBtn}
            style={{ background: name ? '#090909' : '#A9A9B2', cursor: name ? 'pointer' : 'default' }}
          >
            {isEdit ? '수정하기' : '보드 만들기'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ProducerBookmarksPage() {
  const { removeFromMap } = useSavedItems();
  const [view, setView] = useState('board');
  const [selectedBoard, setSelectedBoard] = useState(null);
  const [showNewBoard, setShowNewBoard] = useState(false);
  const [editBoard, setEditBoard] = useState(null);
  const [actorDetail, setActorDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [menuOpen, setMenuOpen] = useState(null);
  const [boards, setBoards] = useState([]);
  const [actors, setActors] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [actorsLoading, setActorsLoading] = useState(false);
  const [genderFilter, setGenderFilter] = useState('전체');
  const [genderOpen, setGenderOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [confirmDeleteBoard, setConfirmDeleteBoard] = useState(null);
  const [confirmDeleteActor, setConfirmDeleteActor] = useState(null);

  const readPreferredDefaultBoardId = useCallback(() => {
    try {
      return localStorage.getItem(PREFERRED_DEFAULT_BOARD_KEY);
    } catch {
      return null;
    }
  }, []);

  const writePreferredDefaultBoardId = useCallback((boardId) => {
    try {
      if (!boardId) {
        localStorage.removeItem(PREFERRED_DEFAULT_BOARD_KEY);
        return;
      }
      localStorage.setItem(PREFERRED_DEFAULT_BOARD_KEY, String(boardId));
    } catch {}
  }, []);

  const loadAllActors = useCallback(async () => {
    setActorsLoading(true);
    try {
      const res = await getAllSavedItems();
      const list = Array.isArray(res) ? res : (res?.content ?? []);
      setActors(
        list
          .filter(item => !item.targetType || item.targetType === 'TALENT_PROFILE')
          .map(mapBoardItem)
      );
    } catch {
      setActors([]);
    } finally {
      setActorsLoading(false);
    }
  }, []);

  const loadBoardActors = useCallback(async (board) => {
    if (!board?.id) {
      setActors([]);
      return;
    }
    setSelectedBoard(board);
    setActors([]);
    setActorsLoading(true);
    try {
      const res = await getBoardItems(board.id);
      const list = Array.isArray(res) ? res : (res?.content ?? []);
      setActors(
        list
          .filter(item => !item.targetType || item.targetType === 'TALENT_PROFILE')
          .map(mapBoardItem)
      );
    } catch {
      setActors([]);
    } finally {
      setActorsLoading(false);
    }
  }, []);

  const loadBoards = useCallback(async () => {
    setIsLoading(true);
    setIsError(false);
    try {
      const preferredDefaultBoardId = readPreferredDefaultBoardId();
      const normalized = await getBoardPickerOptions({
        targetType: 'TALENT_PROFILE',
        preferredDefaultBoardId,
      });
      setBoards(normalized);
      return normalized;
    } catch {
      setIsError(true);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    const openBoardId = (() => {
      try {
        const v = localStorage.getItem('producer-bookmark-open-board');
        if (v) { localStorage.removeItem('producer-bookmark-open-board'); }
        return v;
      } catch { return null; }
    })();
    loadBoards().then(normalized => {
      if (openBoardId) {
        const target = (normalized || []).find(b => String(b.id) === String(openBoardId));
        if (target) {
          loadBoardActors(target);
        }
      }
    });
  }, [loadBoards, loadBoardActors]);

  useEffect(() => {
    if (view !== 'list' || selectedBoard) return;
    loadAllActors();
  }, [view, selectedBoard, loadAllActors]);

  const refreshCurrentView = useCallback(async () => {
    await loadBoards();
    if (selectedBoard?.id) {
      await loadBoardActors(selectedBoard);
      return;
    }
    if (view === 'list') {
      await loadAllActors();
    }
  }, [loadAllActors, loadBoardActors, loadBoards, selectedBoard, view]);

  const handleCreate = async ({ name, desc, color, icon }) => {
    try {
      await createBoard({ name, color: color || COLORS[0], emoji: icon || ICONS[0], description: desc || '' });
      await loadBoards();
    } catch {}
    setShowNewBoard(false);
  };

  const handleEditBoard = async ({ name, desc, color, icon }) => {
    if (!editBoard) return;
    try {
      await updateBoard(editBoard.id, { name, color: color || COLORS[0], emoji: icon || ICONS[0], description: desc || '' });
      await loadBoards();
      if (selectedBoard?.id === editBoard.id) {
        setSelectedBoard((prev) => (prev ? {
          ...prev,
          name,
          desc: desc || '',
          color: color || COLORS[0],
          emoji: icon || ICONS[0],
        } : prev));
      }
    } catch {}
    setEditBoard(null);
  };

  const handleDeleteBoard = async (boardId) => {
    try {
      // 보관함 안 배우 항목 먼저 모두 삭제 후 보관함 삭제
      try {
        const items = await getBoardItems(boardId);
        const list = Array.isArray(items) ? items : (items?.content ?? []);
        await Promise.all(list.map(item => removeBoardItem(item.id)));
        // context talentMap에서도 모두 제거
        list.forEach(item => {
          const tid = item.targetId ?? item.talentProfile?.id ?? item.talent?.id;
          if (tid) removeFromMap(tid, 'TALENT_PROFILE');
        });
      } catch {}
      await deleteBoard(boardId);
      const preferredDefaultBoardId = readPreferredDefaultBoardId();
      if (String(preferredDefaultBoardId) === String(boardId)) {
        writePreferredDefaultBoardId(null);
      }
      setBoards(prev => prev.filter(b => b.id !== boardId));
      if (selectedBoard?.id === boardId) {
        setSelectedBoard(null);
        setActors([]);
      }
    } catch {}
    setConfirmDeleteBoard(null);
  };

  const handleOpenDetail = async (talentProfileId) => {
    if (!talentProfileId) return;
    setDetailLoading(true);
    try {
      const full = await getTalentDetail(talentProfileId);
      let profilePhotos = [];
      try {
        const photoRes = await getTalentMediaPhotos(talentProfileId, 'profile');
        profilePhotos = (photoRes?.photos || []).map((photo) => photo.url).filter(Boolean);
      } catch {
        profilePhotos = [];
      }
      setActorDetail({
        ...full,
        id: full.id ?? talentProfileId,
        name: full.stageName || full.name,
        realName: full.name,
        stageName: full.stageName || '',
        height: full.heightCm ? `${full.heightCm}cm` : '—',
        weight: full.weightKg ? `${full.weightKg}kg` : '—',
        photos: profilePhotos,
      });
    } catch {
      // 상세 조회 실패 시 모달 열지 않음
    } finally {
      setDetailLoading(false);
    }
  };

  const handleRemove = async (actor) => {
    if (!actor?.archiveItemId) return;
    try {
      await removeBoardItem(actor.archiveItemId);
      // context talentMap에서도 제거 → 배우찾기 저장 표시 해제
      if (actor.id) removeFromMap(actor.id, 'TALENT_PROFILE');
      await refreshCurrentView();
    } catch {}
    setConfirmDeleteActor(null);
  };

  const handleMove = async (actor, boardId) => {
    if (!actor?.archiveItemId || !boardId) return;
    try {
      await moveBoardItem(actor.archiveItemId, boardId);
      await refreshCurrentView();
    } catch {
      try {
        await addBoardItem({ boardId, targetType: 'TALENT_PROFILE', targetId: Number(actor.id) });
        await removeBoardItem(actor.archiveItemId);
        await refreshCurrentView();
      } catch {}
    }
  };

  const handleSetDefaultBoard = async (board) => {
    if (!board?.id) return;
    writePreferredDefaultBoardId(board.id);
    await loadBoards();
  };

  const totalCount = boards.reduce((sum, b) => sum + (b.count ?? 0), 0);

  const filteredActors = actors.filter(a => {
    if (genderFilter !== '전체' && a.gender !== genderFilter) return false;
    if (search && !a.name.includes(search) && !a.tags.join(' ').includes(search)) return false;
    return true;
  });

  return (
    <div onClick={() => { setMenuOpen(null); setGenderOpen(false); }}>
        <ProducerContentContainer>
          <div style={{ padding: '50px 40px 0' }}>

          {/* Header */}
          {selectedBoard ? (
            <div>
              <button onClick={() => { setSelectedBoard(null); setActors([]); }} className={styles.backBtn}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M15 18l-6-6 6-6" stroke="#A2A2A2" strokeWidth="1.5" strokeLinecap="round"/></svg>
                뒤로 가기
              </button>
              <div className={styles.boardDetailHeader}>
                <h1 className={styles.pageTitle}>{selectedBoard.name}</h1>
                <span className={styles.pageSubtitle}>총 {filteredActors.length}명의 프로필을 저장했습니다.</span>
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <h1 style={{ fontFamily: 'Pretendard, sans-serif', fontSize: '36px', fontWeight: 600, letterSpacing: '-0.04em', color: '#090909', lineHeight: '140%', margin: 0 }}>보관함</h1>
                <span style={{ fontFamily: 'Pretendard, sans-serif', fontSize: '16px', fontWeight: 400, letterSpacing: '-0.04em', color: '#A2A2A2', lineHeight: '140%' }}>마음에 드는 배우들을 보드로 나누어 체계적으로 관리해보세요.</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                {[
                  { id: 'board', icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><rect x="3" y="3" width="8" height="8" rx="1" stroke="currentColor" strokeWidth="2"/><rect x="13" y="3" width="8" height="8" rx="1" stroke="currentColor" strokeWidth="2"/><rect x="3" y="13" width="8" height="8" rx="1" stroke="currentColor" strokeWidth="2"/><rect x="13" y="13" width="8" height="8" rx="1" stroke="currentColor" strokeWidth="2"/></svg>, label: '보드 보기' },
                  { id: 'list', icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>, label: '전체 목록' },
                ].map(({ id, icon, label }) => (
                  <button key={id} onClick={() => setView(id)} className={styles.viewToggleBtn}
                    style={{
                      background: '#FFFFFF',
                      boxShadow: view === id
                        ? 'inset 2px 2px 12px rgba(0,0,0,0.08)'
                        : '2px 2px 10px rgba(0,0,0,0.08)',
                    }}>
                    {icon} {label}
                  </button>
                ))}
                <button onClick={() => setShowNewBoard(true)} className={styles.newBoardBtn}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
                  새 보드
                </button>
              </div>
            </div>
          )}
          </div>

          <div style={{ padding: '0 40px 60px' }}>
          {/* Search + filter */}
          {(view === 'list' || selectedBoard) && (
            <div className={styles.filterBar} onClick={() => setGenderOpen(false)}>
              <div className={styles.searchBox}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><circle cx="11" cy="11" r="8" stroke="#A2A2A2" strokeWidth="1.5"/><path d="M21 21l-4.35-4.35" stroke="#A2A2A2" strokeWidth="1.5" strokeLinecap="round"/></svg>
                <input
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="배우 이름 또는 특기 검색"
                  className={styles.searchInput}
                />
              </div>
              <div className={styles.filterDropdownWrap} onClick={e => e.stopPropagation()}>
                <button onClick={() => setGenderOpen(p => !p)} className={styles.filterBtn}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none"><path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z" stroke="#090909" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  {genderFilter}
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M6 9l6 6 6-6" stroke="#888896" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </button>
                {genderOpen && (
                  <div className={styles.filterDropdown}>
                    {['전체', '남성', '여성'].map(g => (
                      <button key={g} onClick={() => { setGenderFilter(g); setGenderOpen(false); }}
                        className={styles.filterOption}
                        style={{ background: genderFilter === g ? '#E9E9EC' : 'none', fontWeight: genderFilter === g ? 600 : 400 }}>
                        {g}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* 보드 로딩 / 에러 */}
          {isLoading && (
            <div style={{ padding: '80px 0', display: 'flex', justifyContent: 'center' }}>
              <LoadingSpinner size="lg" />
            </div>
          )}
          {isError && (
            <div style={{ textAlign: 'center', padding: '80px 0' }}>
              <div style={{ fontFamily: 'Pretendard, sans-serif', fontSize: '16px', fontWeight: 600, color: '#090909' }}>
                데이터를 불러오지 못했습니다.
              </div>
              <button onClick={loadBoards} style={{ marginTop: '12px', padding: '8px 20px', borderRadius: '10px', border: '1px solid #E9E9EC', background: '#fff', cursor: 'pointer', fontFamily: 'Pretendard, sans-serif', fontSize: '14px', fontWeight: 500 }}>
                다시 시도
              </button>
            </div>
          )}

          {/* 보드 뷰 */}
          {!isLoading && !isError && view === 'board' && !selectedBoard && (
            <div className={styles.boardGrid}>
              {boards.length === 0 ? (
                <div className={styles.emptyState}>
                  <span className={styles.emptyStateText}>저장된 보드가 없습니다.</span>
                </div>
              ) : boards.map(board => (
                <div key={board.id} className={styles.boardCard} onClick={() => loadBoardActors(board)}>
                  <div className={styles.boardIconWrap}>
                    <div className={styles.boardIcon} style={{ background: board.color }}>{board.emoji}</div>
                    <div className={styles.boardBadge}>{board.count}</div>
                  </div>
                  <div className={styles.boardCardText}>
                    <div className={styles.boardCardTitleRow}>
                      <div className={styles.boardCardName}>{board.name}</div>
                      {board.isDefault && <span className={styles.boardDefaultBadge}>기본</span>}
                    </div>
                    <div className={styles.boardCardDesc}>{board.desc}</div>
                  </div>
                  <div className={styles.boardMenuWrap} onClick={e => e.stopPropagation()}>
                    <button onClick={() => setMenuOpen(menuOpen === board.id ? null : board.id)} className={styles.boardMenuBtn}>···</button>
                    {menuOpen === board.id && (
                      <div className={styles.boardMenuDropdown}>
                        {[
                          ['보기', null, () => { loadBoardActors(board); setMenuOpen(null); }, (
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                              <ellipse cx="12" cy="12" rx="9" ry="6" stroke="#090909" strokeWidth="1.5"/>
                              <circle cx="12" cy="12" r="2.5" stroke="#090909" strokeWidth="1.5"/>
                            </svg>
                          )],
                          ['수정', null, () => { setEditBoard(board); setMenuOpen(null); }, (
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                              <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" stroke="#090909" strokeWidth="1.5" strokeLinecap="round"/>
                              <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" stroke="#090909" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          )],
                          ...(!board.isDefault ? [['기본 보관함 설정', null, () => { handleSetDefaultBoard(board); setMenuOpen(null); }, (
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                              <path d="M12 17.27L18.18 21 16.54 13.97 22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21 12 17.27z" stroke="#090909" strokeWidth="1.5" strokeLinejoin="round"/>
                            </svg>
                          )]] : []),
                          ...(!board.isDefault ? [['삭제', '#EF4444', () => { setConfirmDeleteBoard(board); setMenuOpen(null); }, (
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                              <path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6" stroke="#EF4444" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          )]] : []),
                        ].map(([label, color, action, icon]) => (
                          <button key={label} onClick={action} className={styles.boardMenuOption} style={{ color: color || '#090909' }}>
                            {icon}
                            {label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* 리스트 뷰 / 보드 상세 */}
          {!isLoading && !isError && (view === 'list' || selectedBoard) && (
            actorsLoading ? (
              <div style={{ padding: '60px 0', display: 'flex', justifyContent: 'center' }}>
                <LoadingSpinner size="md" />
              </div>
            ) : filteredActors.length === 0 ? (
              <div className={styles.emptyState}>
                <span className={styles.emptyStateText}>
                  {search || genderFilter !== '전체' ? '검색 결과가 없습니다.' : '저장된 배우가 없습니다.'}
                </span>
              </div>
            ) : (
              <div className={styles.actorGrid}>
                {filteredActors.map(actor => (
                  <ActorCard
                    key={actor.archiveItemId ?? actor.id}
                    actor={actor}
                    boards={boards}
                    currentBoardId={selectedBoard?.id ?? actor.boardId}
                    onDetail={handleOpenDetail}
                    onRemove={(a) => setConfirmDeleteActor(a)}
                    onMove={handleMove}
                  />
                ))}
              </div>
            )
          )}
          </div>
        </ProducerContentContainer>

        {confirmDeleteBoard && createPortal(
          <div onClick={() => setConfirmDeleteBoard(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
            <div onClick={e => e.stopPropagation()} style={{ width: '100%', maxWidth: '360px', background: '#fff', borderRadius: '20px', padding: '28px 24px', boxSizing: 'border-box' }}>
              <h4 style={{ margin: '0 0 10px 0', fontSize: '18px', fontWeight: 700, color: '#090909', letterSpacing: '-0.04em' }}>정말로 삭제하시겠습니까?</h4>
              <p style={{ margin: '0 0 24px 0', fontSize: '14px', color: '#666', lineHeight: '1.6', letterSpacing: '-0.04em' }}>
                <strong style={{ color: '#090909' }}>{confirmDeleteBoard.name}</strong> 보관함이 삭제됩니다.
                {(confirmDeleteBoard.count ?? 0) > 0 && (
                  <><br /><span style={{ color: '#EF4444' }}>저장된 배우 {confirmDeleteBoard.count}명도 함께 삭제됩니다.</span></>
                )}
              </p>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button onClick={() => setConfirmDeleteBoard(null)} style={{ flex: 1, padding: '13px', borderRadius: '12px', border: '1px solid #E9E9EC', background: '#fff', cursor: 'pointer', fontSize: '15px', fontWeight: 600, fontFamily: 'Pretendard, sans-serif', color: '#090909', letterSpacing: '-0.04em' }}>
                  취소
                </button>
                <button onClick={() => handleDeleteBoard(confirmDeleteBoard.id)} style={{ flex: 1, padding: '13px', borderRadius: '12px', border: 'none', background: '#EF4444', cursor: 'pointer', fontSize: '15px', fontWeight: 600, fontFamily: 'Pretendard, sans-serif', color: '#fff', letterSpacing: '-0.04em' }}>
                  확인
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
        {confirmDeleteActor && createPortal(
          <div onClick={() => setConfirmDeleteActor(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
            <div onClick={e => e.stopPropagation()} style={{ width: '100%', maxWidth: '360px', background: '#fff', borderRadius: '20px', padding: '28px 24px', boxSizing: 'border-box' }}>
              <h4 style={{ margin: '0 0 10px 0', fontSize: '18px', fontWeight: 700, color: '#090909', letterSpacing: '-0.04em' }}>정말로 삭제하시겠습니까?</h4>
              <p style={{ margin: '0 0 24px 0', fontSize: '14px', color: '#666', lineHeight: '1.6', letterSpacing: '-0.04em' }}>
                <strong style={{ color: '#090909' }}>{confirmDeleteActor.name}</strong> 배우가 보관함에서 삭제됩니다.
              </p>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button onClick={() => setConfirmDeleteActor(null)} style={{ flex: 1, padding: '13px', borderRadius: '12px', border: '1px solid #E9E9EC', background: '#fff', cursor: 'pointer', fontSize: '15px', fontWeight: 600, fontFamily: 'Pretendard, sans-serif', color: '#090909', letterSpacing: '-0.04em' }}>
                  취소
                </button>
                <button onClick={() => handleRemove(confirmDeleteActor)} style={{ flex: 1, padding: '13px', borderRadius: '12px', border: 'none', background: '#EF4444', cursor: 'pointer', fontSize: '15px', fontWeight: 600, fontFamily: 'Pretendard, sans-serif', color: '#fff', letterSpacing: '-0.04em' }}>
                  확인
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
        {showNewBoard && createPortal(
          <NewBoardModal
            onClose={() => setShowNewBoard(false)}
            onCreate={handleCreate}
          />,
          document.body
        )}
        {editBoard && createPortal(
          <NewBoardModal
            initialData={editBoard}
            onClose={() => setEditBoard(null)}
            onCreate={handleEditBoard}
          />,
          document.body
        )}
        {detailLoading && createPortal(
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)', zIndex: 999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ background: '#fff', borderRadius: '12px', padding: '24px 40px', display: 'flex', justifyContent: 'center' }}><LoadingSpinner size="sm" /></div>
          </div>,
          document.body
        )}
        {actorDetail && <ActorDetailModal actor={actorDetail} onClose={() => setActorDetail(null)} />}
      </div>
  );
}
